import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { inventoryAPI, leadsAPI, settingsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ContactPopup } from '@/components/ContactPopup';
import { SEO, buildVehicleSchema, buildBreadcrumbSchema } from '@/components/SEO';
import {
  ArrowLeft, Gauge, MapPin, Calendar, Truck, Share2, DollarSign,
  GitCompare, ChevronLeft, ChevronRight, Phone, Cog, Zap, Play, Printer, RotateCw, Download, X, ZoomIn
} from 'lucide-react';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";
const formatPrice = (p) => p ? `$${p.toLocaleString()}` : 'Call for Price';
const formatKM = (km) => km ? `${km.toLocaleString()} KM` : 'N/A';

function Lightbox({ images, activeIndex, onClose, onNext, onPrev, title }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => { window.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="lightbox-overlay" onClick={onClose}>
      <div className="absolute top-4 right-4 z-10 flex items-center gap-3">
        <span className="text-white/70 text-sm">{activeIndex + 1} / {images.length}</span>
        <button onClick={onClose} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="lightbox-close">
          <X className="w-6 h-6" />
        </button>
      </div>
      {images.length > 1 && (
        <>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); onPrev(); }} data-testid="lightbox-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors" onClick={(e) => { e.stopPropagation(); onNext(); }} data-testid="lightbox-next">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <img
        src={resolveImageUrl(images[activeIndex])}
        alt={`${title} - Photo ${activeIndex + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
    </div>
  );
}

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`;
  // Vimeo
  const vmMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;
  // Direct video URL (.mp4, .webm, etc.)
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return url;
  return url;
}

function isDirectVideo(url) {
  return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
}

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [leadDialog, setLeadDialog] = useState(null);
  const [contactPopup, setContactPopup] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [view360, setView360] = useState(false);
  const [spin360Idx, setSpin360Idx] = useState(0);
  const dragRef = useRef({ active: false, startX: 0, lastIdx: 0 });
  const spin360ContainerRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const imgCount = item?.images?.length || 0;

  const handle360Start = useCallback((clientX) => {
    dragRef.current = { active: true, startX: clientX, lastIdx: spin360Idx };
  }, [spin360Idx]);

  const handle360Move = useCallback((clientX) => {
    if (!dragRef.current.active || imgCount < 2) return;
    const dx = clientX - dragRef.current.startX;
    const sensitivity = 8;
    const steps = Math.floor(dx / sensitivity);
    const newIdx = ((dragRef.current.lastIdx - steps) % imgCount + imgCount) % imgCount;
    setSpin360Idx(newIdx);
  }, [imgCount]);

  const handle360End = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  useEffect(() => {
    inventoryAPI.get(id).then(r => { setItem(r.data); setLoading(false); }).catch(() => { setLoading(false); navigate('/inventory'); });
    settingsAPI.getBranding().then(r => {
      if (r.data?.primary_logo) setLogoUrl(resolveImageUrl(r.data.primary_logo));
    }).catch(() => {});
  }, [id, navigate]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await leadsAPI.create({ ...leadForm, type: leadDialog, inventory_id: id });
      toast.success('Request submitted! We will contact you shortly.');
      setLeadDialog(null);
      setLeadForm({ name: '', email: '', phone: '', message: '' });
    } catch { toast.error('Failed to submit. Please try again.'); }
    setSubmitting(false);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><div className="animate-pulse h-96 bg-zinc-100 rounded-sm" /></div>;
  if (!item) return null;

  const images = item.images?.length > 0 ? item.images : [];
  const hasImages = images.length > 0;
  const videoUrl = item.video_url ? getEmbedUrl(item.video_url) : null;
  const hasVideo = !!videoUrl;

  // Media items: video first (index 0), then photos
  const mediaCount = (hasVideo ? 1 : 0) + images.length;
  const isVideoSlide = (idx) => hasVideo && idx === 0;
  const getImageForSlide = (idx) => resolveImageUrl(hasVideo ? images[idx - 1] : images[idx]);

  const handlePrintSpec = () => {
    window.open(`/inventory/${item.id}/print`, '_blank');
  };

  const handleDownloadPDF = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.id}/spec-sheet`;
    window.open(url, '_blank');
  };

  const specs = [
    // Truck specs
    ...(item.type === 'truck' ? [
      { label: 'Engine', value: item.engine, icon: Cog },
      { label: 'Horsepower', value: item.horsepower ? `${item.horsepower} HP` : null, icon: Zap },
      { label: 'Transmission', value: item.transmission, icon: Cog },
      { label: 'Rear Ratio', value: item.rear_ratio },
      { label: 'Axles', value: item.axles },
      { label: 'Wheelbase', value: item.wheelbase },
      { label: 'Suspension', value: item.suspension },
      { label: 'GVWR', value: item.gvwr },
      { label: 'Sleeper / Day Cab', value: item.sleeper_daycab },
      { label: 'Color', value: item.color },
    ] : []),
    // Trailer specs
    ...(item.type === 'trailer' ? [
      { label: 'Trailer Type', value: item.trailer_type },
      { label: 'Axle', value: item.axles },
      { label: 'Suspension', value: item.suspension },
      { label: 'Floor Type', value: item.floor_type },
      { label: 'Length', value: item.length || item.trailer_length },
      { label: 'Width', value: item.width },
    ] : []),
    // Dry Van specs
    ...(item.type === 'dry_van' ? [
      { label: 'Floor Type', value: item.floor_type },
      { label: 'Length', value: item.length || item.trailer_length },
      { label: 'Width', value: item.width },
      { label: 'Axle', value: item.axles },
      { label: 'Suspension', value: item.suspension },
      { label: 'Roof Type', value: item.roof_type },
      { label: 'Door Type', value: item.door_type },
    ] : []),
    // Reefer specs
    ...(item.type === 'reefer' ? [
      { label: 'Reefer Unit', value: item.reefer_unit },
      { label: 'Reefer Hours', value: item.reefer_hours ? `${Number(item.reefer_hours).toLocaleString()} hrs` : null },
      { label: 'Axle', value: item.axles },
      { label: 'Floor Type', value: item.floor_type },
      { label: 'Length', value: item.length || item.trailer_length },
      { label: 'Width', value: item.width },
      { label: 'Suspension', value: item.suspension },
      { label: 'Door Type', value: item.door_type },
    ] : []),
    // Flatbed specs
    ...(item.type === 'flatbed' ? [
      { label: 'Axle', value: item.axles },
      { label: 'Floor Type', value: item.floor_type },
      { label: 'Length', value: item.length || item.trailer_length },
      { label: 'Width', value: item.width },
      { label: 'Suspension', value: item.suspension },
    ] : []),
  ].filter(s => s.value);

  // Compute a safe display item for SEO/schema — strip price when not public.
  const priceIsPublic = item.price && item.price_public !== false;
  const seoItem = priceIsPublic ? item : { ...item, price: undefined };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="inventory-detail-page">
      <SEO
        title={`${item.title}${priceIsPublic ? ` - $${item.price.toLocaleString()}` : ' - Call for Price'} | Used ${item.make} ${item.category || 'Truck'}`}
        description={item.description || `${item.year} ${item.make} ${item.model} for sale at Canadian Truck Sales, Mississauga ON. ${item.engine ? `Engine: ${item.engine}. ` : ''}${item.mileage_km ? `Mileage: ${item.mileage_km.toLocaleString()} KM. ` : ''}${priceIsPublic ? `Price: $${item.price.toLocaleString()} CAD. ` : ''}Financing available. Best prices in the GTA. Call (416) 417-2525.`}
        url={`/inventory/${item.id}`}
        image={images.length > 0 ? resolveImageUrl(images[0]) : undefined}
        type="product"
        keywords={`${item.year} ${item.make} ${item.model} for sale, used ${item.make} ${item.model}, used ${item.make} trucks Ontario, ${item.make} ${item.model} price, buy ${item.make} ${item.model} Canada, ${item.make} dealer Mississauga, used ${item.category || 'truck'} for sale Ontario`}
        jsonLd={[
          buildVehicleSchema(seoItem),
          buildBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Inventory', url: '/inventory' }, { name: item.title, url: `/inventory/${item.id}` }])
        ]}
      />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} data-testid="back-to-inventory">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Inventory
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Gallery */}
        <div className="lg:col-span-3" data-testid="photo-gallery">
          {/* 360 View Mode */}
          {view360 && images.length >= 2 ? (
            <div className="relative aspect-video rounded-sm overflow-hidden bg-[#0A0A0A] select-none" data-testid="360-viewer">
              <img
                src={resolveImageUrl(images[spin360Idx])}
                alt={`${item.title} - 360 view`}
                className="w-full h-full object-cover"
                draggable={false}
              />
              {/* 360 interaction overlay */}
              <div
                ref={spin360ContainerRef}
                className="absolute inset-0 cursor-grab active:cursor-grabbing z-10"
                onMouseDown={e => { e.preventDefault(); handle360Start(e.clientX); }}
                onMouseMove={e => handle360Move(e.clientX)}
                onMouseUp={handle360End}
                onMouseLeave={handle360End}
                onTouchStart={e => handle360Start(e.touches[0].clientX)}
                onTouchMove={e => handle360Move(e.touches[0].clientX)}
                onTouchEnd={handle360End}
              />
              {/* 360 badge */}
              <div className="absolute top-3 left-3 bg-black/80 text-white px-3 py-1.5 rounded-full flex items-center gap-2 z-20">
                <RotateCw className="w-4 h-4 animate-spin" style={{ animationDuration: '3s' }} />
                <span className="text-xs font-semibold tracking-wide">360&deg; VIEW</span>
              </div>
              {/* Counter */}
              <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded z-20">
                {spin360Idx + 1} / {images.length}
              </div>
              {/* Drag hint */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white/70 text-[11px] px-3 py-1 rounded-full z-20 flex items-center gap-1.5">
                <ChevronLeft className="w-3 h-3" />
                <span>Drag to rotate</span>
                <ChevronRight className="w-3 h-3" />
              </div>
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-20">
                <div className="h-full bg-[#E31937] transition-all duration-100" style={{ width: `${((spin360Idx + 1) / images.length) * 100}%` }} />
              </div>
              {/* Close button */}
              <button
                className="absolute top-3 right-3 bg-white/90 text-[#0A0A0A] px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-white z-20 transition-colors"
                onClick={() => setView360(false)}
                data-testid="close-360-btn"
              >
                Exit 360&deg;
              </button>
            </div>
          ) : (
          <div className="relative aspect-video rounded-sm overflow-hidden bg-zinc-200">
            {!hasImages && !hasVideo ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-200">
                <img src={logoUrl} alt="CTS" className="w-1/2 opacity-30" />
                <span className="mt-3 text-lg font-semibold text-zinc-400 uppercase tracking-wider">Coming Soon</span>
              </div>
            ) : isVideoSlide(activeImg) ? (
              isDirectVideo(item.video_url) ? (
                <video src={videoUrl} controls autoPlay muted className="w-full h-full object-cover" data-testid="gallery-video" />
              ) : (
                <iframe src={videoUrl} title="Vehicle Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-testid="gallery-video" />
              )
            ) : (
              <>
                <img
                  src={getImageForSlide(activeImg)}
                  alt={item.title}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setLightbox({ open: true, index: hasVideo ? activeImg - 1 : activeImg })}
                  data-testid="gallery-main-image"
                />
                {/* Zoom hint */}
                <div className="absolute bottom-12 right-3 bg-black/50 text-white/70 text-[11px] px-2.5 py-1 rounded-full z-[6] flex items-center gap-1 pointer-events-none">
                  <ZoomIn className="w-3 h-3" /> Click to enlarge
                </div>
                {/* Logo — only when images exist, shifts left when ribbon present */}
                <div className={`absolute top-3 ${item.status && item.status !== 'Available' ? 'left-3' : 'right-3'} z-[5] pointer-events-none`}>
                  <div className="bg-white/95 backdrop-blur-sm rounded px-2.5 py-1.5 shadow">
                    <img src={logoUrl} alt="CTS" className="h-9 w-auto" />
                  </div>
                </div>
                {/* Center watermark */}
                <img src={logoUrl} alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 opacity-[0.18] pointer-events-none z-[1] drop-shadow-sm" />
              </>
            )}
            {item.status && item.status !== 'Available' && (
              <div className="absolute top-0 right-0 overflow-hidden w-40 h-40 pointer-events-none z-10" data-testid="detail-ribbon">
                <div className="absolute top-[18px] right-[-42px] w-[200px] text-center text-base font-extrabold uppercase tracking-widest text-white py-2.5 rotate-45 shadow-lg bg-[#E31937]" style={{textShadow: '0 1px 3px rgba(0,0,0,0.4)', backgroundImage: 'linear-gradient(to right, #c0102e, #E31937, #ff2d4f, #E31937, #c0102e)'}}>
                  {item.status}
                </div>
              </div>
            )}
            {mediaCount > 1 && (
              <>
                <button className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/80" onClick={() => setActiveImg(i => (i - 1 + mediaCount) % mediaCount)}>
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/80" onClick={() => setActiveImg(i => (i + 1) % mediaCount)}>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
              {activeImg + 1} / {mediaCount}
            </div>
          </div>
          )}
          {/* Thumbnails + 360 button */}
          <div className="flex gap-2 mt-3 overflow-x-auto items-center">
            {images.length >= 2 && (
              <button
                className={`w-20 h-14 rounded-sm overflow-hidden flex-shrink-0 border-2 relative flex items-center justify-center ${view360 ? 'border-[#E31937] bg-[#0A0A0A]' : 'border-gray-200 bg-zinc-100 hover:bg-zinc-200'} transition-colors`}
                onClick={() => { setView360(!view360); setSpin360Idx(0); }}
                data-testid="toggle-360-btn"
              >
                <RotateCw className={`w-4 h-4 ${view360 ? 'text-[#E31937]' : 'text-zinc-500'}`} />
                <span className={`text-[10px] font-bold ml-1 ${view360 ? 'text-white' : 'text-zinc-500'}`}>360&deg;</span>
              </button>
            )}
            {!view360 && Array.from({ length: mediaCount }).map((_, i) => (
              <button
                key={i}
                className={`w-20 h-14 rounded-sm overflow-hidden flex-shrink-0 border-2 relative ${i === activeImg ? 'border-[#E31937]' : 'border-transparent'}`}
                onClick={() => setActiveImg(i)}
              >
                {isVideoSlide(i) ? (
                  <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <img src={getImageForSlide(i)} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Info panel */}
        <div className="lg:col-span-2" data-testid="info-panel">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-[10px]">{item.stock_number}</Badge>
            <Badge className={item.status === 'Available' ? 'bg-green-600' : item.status === 'Hold' ? 'bg-amber-500' : 'bg-zinc-500'}>
              {item.status}
            </Badge>
            <Badge variant="outline" className="capitalize">{item.type}</Badge>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-[#0A0A0A] uppercase">{item.title}</h1>

          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-zinc-500">
            {item.engine && <span className="flex items-center gap-1"><Cog className="w-4 h-4" /><b className="text-zinc-700">Engine:</b> {item.engine}</span>}
            {item.mileage_km && <span className="flex items-center gap-1"><Gauge className="w-4 h-4" /><b className="text-zinc-700">Mileage:</b> {formatKM(item.mileage_km)}</span>}
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /><b className="text-zinc-700">Location:</b> Mississauga, ON</span>
          </div>

          {item.status === 'Sold' ? (
            <p className="text-3xl font-bold text-zinc-500 mt-4 uppercase">Sold</p>
          ) : (item.price && item.price_public !== false) ? (
            <p className="text-3xl font-bold text-[#E31937] mt-4">{formatPrice(item.price)}</p>
          ) : (
            <button
              className="text-3xl font-bold text-[#E31937] mt-4 hover:underline cursor-pointer"
              onClick={() => setContactPopup(true)}
              data-testid="call-for-price-btn"
            >
              Call for Price
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <Button className="flex-1 bg-[#E31937] hover:bg-[#C4122C] text-white font-bold uppercase text-xs h-10" onClick={() => setLeadDialog('pricing')} data-testid="get-price-btn">
              <DollarSign className="w-4 h-4 mr-1" /> Get Best Price
            </Button>
            <Button variant="outline" className="flex-1 border-[#E31937] text-[#E31937] hover:bg-[#E31937] hover:text-white font-bold uppercase text-xs h-10" onClick={() => setLeadDialog('viewing')} data-testid="book-viewing-btn">
              <Phone className="w-4 h-4 mr-1" /> Book Viewing
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => navigate(`/financing?unit=${encodeURIComponent(item.title)}&id=${item.id}`)} data-testid="request-financing-btn">
              <DollarSign className="w-3 h-3 mr-1" /> Request Financing
            </Button>
            <Button variant="outline" size="sm" className="text-xs" onClick={handlePrintSpec} data-testid="print-spec-btn">
              <Printer className="w-3 h-3 mr-1" /> Spec Sheet
            </Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={handleShare} data-testid="share-btn">
              <Share2 className="w-3 h-3 mr-1" /> Share
            </Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate(`/compare?ids=${item.id}`)} data-testid="compare-btn">
              <GitCompare className="w-3 h-3 mr-1" /> Compare
            </Button>
          </div>

          {/* Specs */}
          {specs.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-3">Specifications</h3>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {specs.map(s => (
                  <div key={s.label} className="flex justify-between text-sm py-1 border-b border-zinc-100">
                    <span className="text-zinc-500">{s.label}</span>
                    <span className="font-medium text-[#0A0A0A] text-right">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.description && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-sm font-semibold uppercase text-zinc-500 mb-2">Description</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Lead Dialog */}
      <Dialog open={!!leadDialog} onOpenChange={() => setLeadDialog(null)}>
        <DialogContent data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase">
              {leadDialog === 'pricing' ? 'Get Best Price' : 'Book a Viewing'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-3">
            <Input placeholder="Full Name *" required value={leadForm.name} onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))} data-testid="lead-name" />
            <Input placeholder="Email *" type="email" required value={leadForm.email} onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))} data-testid="lead-email" />
            <Input placeholder="Phone" value={leadForm.phone} onChange={e => setLeadForm(f => ({ ...f, phone: e.target.value }))} data-testid="lead-phone" />
            <Textarea placeholder="Message" value={leadForm.message} onChange={e => setLeadForm(f => ({ ...f, message: e.target.value }))} data-testid="lead-message" />
            <p className="text-xs text-zinc-400">Re: {item.title} ({item.stock_number})</p>
            <Button type="submit" className="w-full bg-[#E31937] hover:bg-[#C4122C]" disabled={submitting} data-testid="lead-submit">
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Popup */}
      <ContactPopup open={contactPopup} onOpenChange={setContactPopup} />

      {/* Lightbox */}
      {lightbox.open && hasImages && (
        <Lightbox
          images={images}
          activeIndex={lightbox.index}
          title={item.title}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onNext={() => setLightbox(lb => ({ ...lb, index: (lb.index + 1) % images.length }))}
          onPrev={() => setLightbox(lb => ({ ...lb, index: (lb.index - 1 + images.length) % images.length }))}
        />
      )}
    </div>
  );
}
